/**
 * Submit Review tab. Owns the order query, the order card, the dropzone, the
 * contact fields and the success state.
 */

import { ApiError, api } from '../api.js';
import { orderNumberRegex, config, t } from '../config.js';
import { $, byHook, hide, show } from '../dom.js';
import { createDropzone } from './dropzone.js';

const SCENARIO_HANDLERS = {
    // Block as early as "Query order" — same messages the user used to see
    // only after hitting Submit.
    pending:        { allow: false, level: 'error', key: 'duplicatedPending' },
    rejected_self:  { allow: true,  level: 'info',  key: 'duplicatedRejected' },
    approved:       { allow: false, level: 'error', key: 'duplicatedApproved' },
    occupied_other: { allow: false, level: 'error', key: 'duplicatedOther' },
    free:           { allow: true },
};

export const createOrderForm = (root, { toast, onLoginRequired } = {}) => {
    const form = byHook('form', root);
    const orderInput = byHook('order-input', root);
    const queryBtn = byHook('query', root);

    const orderCard = byHook('order-card', root);
    const orderImage = byHook('order-image', root);
    const orderTitleLink = byHook('order-title-link', root);
    const orderCta = byHook('order-cta', root);

    const uploadSection = byHook('upload-section', root);
    const submitBtn = byHook('submit', root);
    const submitLabel = $('.marc__btn-label', submitBtn);
    const submitSpinner = $('.marc__btn-spinner', submitBtn);

    const successPanel = byHook('success', root);
    const restartBtn = byHook('restart', root);

    const dropzone = createDropzone(root, {
        onChange(files) {
            submitBtn.disabled = files.length === 0;
        },
        onError(message) {
            toast?.show(message, 'error');
        },
    });

    let currentOrder = null;

    const setLoading = (loading) => {
        submitBtn.disabled = loading || dropzone.getFiles().length === 0;
        submitSpinner.hidden = !loading;
        submitLabel.hidden = loading;
    };

    const showOrder = (order) => {
        currentOrder = order;
        const imageUrl = order.product_image ?? '';
        // The fallback URL we synthesise server-side (m.media-amazon.com/images/P/<ASIN>...)
        // 404s for a small share of catalog items. Hide the <img> on load
        // failure so the card lays out cleanly rather than showing a broken
        // image icon.
        orderImage.style.display = imageUrl ? '' : 'none';
        orderImage.onerror = () => { orderImage.style.display = 'none'; };
        orderImage.onload  = () => { orderImage.style.display = ''; };
        orderImage.src = imageUrl;
        orderImage.alt = order.product_title ?? '';
        orderTitleLink.textContent = order.product_title ?? order.order_number ?? '';
        orderTitleLink.href = order.product_url ?? '#';
        orderCta.href = order.product_url ?? '#';
        show(orderCard);
        show(uploadSection);
    };

    const reset = () => {
        currentOrder = null;
        dropzone.clear();
        orderInput.value = '';
        $('#marc-name', root).value = '';
        $('#marc-phone', root).value = '';
        $('#marc-email', root).value = '';
        hide(orderCard);
        hide(uploadSection);
        hide(successPanel);
        show(form);
        toast?.hide();
    };

    const validateOrderNumber = () => {
        const value = orderInput.value.trim().toUpperCase();
        orderInput.value = value;

        if (value === '') {
            toast?.show(t('orderRequired', 'Please enter an order number.'), 'error');
            return null;
        }
        if (!orderNumberRegex.test(value)) {
            toast?.show(t('orderInvalid', 'Order number format looks invalid.'), 'error');
            return null;
        }
        return value;
    };

    const handleQuery = async () => {
        if (!config.isLogged) {
            toast?.show(t('loginRequired', 'Please sign in to continue.'), 'info');
            onLoginRequired?.();
            return;
        }

        const orderNumber = validateOrderNumber();
        if (!orderNumber) return;

        toast?.hide();
        hide(orderCard);
        hide(uploadSection);

        queryBtn.disabled = true;
        const originalText = queryBtn.textContent;
        queryBtn.textContent = t('querying', 'Querying…');

        try {
            const data = await api.queryOrder(orderNumber);
            const order = data?.order;
            const dup =
                data?.eligibility ??
                // Older plugin versions — fall back so the page still works.
                (await api.checkOrder(orderNumber));

            if (!order) {
                toast?.show(t('queryFailed', 'Could not find that order.'), 'error');
                return;
            }

            const handler = SCENARIO_HANDLERS[dup.scenario] ?? SCENARIO_HANDLERS.free;

            if (handler.key) {
                toast?.show(t(handler.key, ''), handler.level);
            }
            if (!handler.allow) {
                return;
            }

            showOrder(order);
        } catch (error) {
            if (error instanceof ApiError && error.code === 'login_required') {
                onLoginRequired?.();
                return;
            }
            const message = error instanceof ApiError
                ? error.message
                : t('queryFailed', 'Could not find that order.');
            toast?.show(message, 'error');
        } finally {
            queryBtn.disabled = false;
            queryBtn.textContent = originalText;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!currentOrder) return;

        const files = dropzone.getFiles();
        if (files.length === 0) return;

        setLoading(true);

        try {
            await api.submitReview({
                orderNumber: currentOrder.order_number,
                orderInfo: currentOrder,
                screenshots: files,
                name:      $('#marc-name', root).value.trim(),
                phone:     $('#marc-phone', root).value.trim(),
                email:     $('#marc-email', root).value.trim(),
            });

            hide(form);
            show(successPanel);
            toast?.hide();

            // Bump the "Awaiting review" stat in the balance card so the
            // user gets immediate feedback that their submission counts.
            // Approved/balance numbers don't change until an admin clicks
            // approve; we deliberately leave those as-is.
            const pendingCell = root.querySelector(
                '[data-marc-balance] .marc__balance-stat:nth-child(2) .marc__balance-stat-value'
            );
            if (pendingCell) {
                const next = (parseInt(pendingCell.textContent.replace(/\D/g, ''), 10) || 0) + 1;
                pendingCell.textContent = String(next);
            }
        } catch (error) {
            if (error instanceof ApiError && error.code === 'login_required') {
                onLoginRequired?.();
                return;
            }
            const message = error instanceof ApiError
                ? error.message
                : t('submitFailed', 'Submission failed.');
            toast?.show(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    queryBtn.addEventListener('click', handleQuery);
    orderInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleQuery();
        }
    });
    form.addEventListener('submit', handleSubmit);
    restartBtn.addEventListener('click', reset);

    return {
        reset,
        prefillOrder(orderNumber) {
            orderInput.value = orderNumber;
            handleQuery();
        },
    };
};
